import { Component, type ReactNode, useCallback, useEffect, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import {
  BookmarkCheck,
  Check,
  FileDown,
  ListChecks,
  Search,
  ShoppingBasket,
  Sparkles,
  Star,
  Timer,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  getStripe,
  getStripeEnvironment,
  isPaymentsConfigured,
  isPaymentsTestMode,
} from "@/lib/stripe";
import { getRememberedEmail } from "@/lib/rememberedEmail";
import { useFoundingStatus } from "@/hooks/useFoundingStatus";
import {
  PRICES,
  STANDARD_ANCHOR,
  resolvePriceId,
  yearlySavingsLabel,
  type BillingInterval,
  type PlanTier,
  type PriceId,
} from "@/lib/pricing";

export type UpsellVariant = "default" | "save-limit" | "menus" | "expired-trial" | "timers";

type Props = {
  /** When true, mount the embedded Stripe checkout iframe immediately. */
  showCheckout: boolean;
  email: string;
  returnUrl?: string;
  variant?: UpsellVariant;
  /** Mobile page only — invoked when the user taps the primary CTA. */
  onRevealCheckout?: (priceId: PriceId) => void;
  /** Pre-selected price ID when re-mounting checkout (e.g. mobile page). */
  initialPriceId?: PriceId;
};

const PaymentsUnavailableNotice = () => (
  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
    <p className="font-semibold">Payments are temporarily unavailable</p>
    <p className="mt-1 text-muted-foreground">
      We couldn't load the secure checkout right now. Please try again in a moment, or
      contact support if this keeps happening.
    </p>
  </div>
);

class CheckoutErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.error("[UpsellContent] embedded checkout failed", error);
  }
  render() {
    if (this.state.hasError) return <PaymentsUnavailableNotice />;
    return this.props.children;
  }
}

const FEATURES: Array<{ icon: typeof BookmarkCheck; title: string; body: string }> = [
  { icon: BookmarkCheck, title: "Unlimited recipes", body: "Save as many as you like — no 5-recipe cap." },
  { icon: ListChecks, title: "Build weekly menus in seconds", body: "Collect recipes into menus like \"This Week's Menu\"." },
  { icon: ShoppingBasket, title: "Instant smart shopping lists", body: "Auto-generated, merged, & grouped by aisle." },
  { icon: Timer, title: "Smart cooking timers", body: "Tap any time in a step to start a hands-free timer that keeps the screen awake." },
  { icon: FileDown, title: "Export recipes to PDF", body: "Save or print individual recipes & your full collection." },
  { icon: Search, title: "Organised, searchable library", body: "Find any recipe by title, ingredient, or tag." },
  { icon: Sparkles, title: "Save time, reduce food waste", body: "Plan once, shop once, cook all week." },
];

export const UpsellContent = ({
  showCheckout,
  email,
  returnUrl,
  variant = "default",
  onRevealCheckout,
  initialPriceId,
}: Props) => {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [interval, setInterval] = useState<BillingInterval>("yearly");
  const founding = useFoundingStatus();
  const paymentsReady = isPaymentsConfigured();

  // Default tier: Founding when spots available, otherwise Standard.
  const [tier, setTier] = useState<PlanTier>("founding");
  useEffect(() => {
    if (!founding.loading && founding.soldOut) setTier("standard");
  }, [founding.loading, founding.soldOut]);

  const selectedPriceId: PriceId = initialPriceId || resolvePriceId(tier, interval);

  useEffect(() => {
    if (!showCheckout) setErrorMsg(null);
  }, [showCheckout]);

  const customerEmail = (email || getRememberedEmail() || "").toLowerCase().trim();
  const computedReturnUrl =
    returnUrl ||
    `${window.location.origin}/recipe?upgraded=1&session_id={CHECKOUT_SESSION_ID}`;

  // For the "save-limit" variant we only want to claim the user has hit the
  // free 5-recipe cap when that's actually true. A user can land here via the
  // crown link with very few (or zero) saved recipes — in that case we show
  // a softer, generic line instead of a misleading hard claim.
  const [recipeCount, setRecipeCount] = useState<number | null>(null);
  useEffect(() => {
    if (variant !== "save-limit" || !customerEmail) {
      setRecipeCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("count_recipes_for_email", {
        _email: customerEmail,
      });
      if (!cancelled) setRecipeCount(typeof data === "number" ? data : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [variant, customerEmail]);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceId: selectedPriceId,
        customerEmail,
        environment: getStripeEnvironment(),
        returnUrl: computedReturnUrl,
      },
    });
    if (error || !data?.clientSecret) {
      const msg = error?.message || data?.error || "Couldn't start checkout.";
      setErrorMsg(msg);
      throw new Error(msg);
    }
    return data.clientSecret;
  }, [customerEmail, computedReturnUrl, selectedPriceId]);

  const saveLimitChip =
    recipeCount !== null && recipeCount >= 5
      ? "You've hit the free 5-recipe limit."
      : "Unlock unlimited recipes & the full toolkit.";

  const contextChip =
    variant === "save-limit"
      ? saveLimitChip
      : variant === "menus"
        ? "Menus & shopping lists are a Premium feature."
        : variant === "expired-trial"
          ? "Your free trial has ended."
          : variant === "timers"
            ? "Smart cooking timers are a Premium feature."
            : null;

  const showFoundingCallout = !founding.loading && !founding.soldOut && tier === "founding";
  const primaryCta = variant === "expired-trial" ? "Unlock Full Access" : "Start 14-Day Free Trial";
  const activePrice = PRICES[selectedPriceId];

  return (
    <div className="space-y-4">
      {contextChip && (
        <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-medium text-foreground">
          {contextChip}
        </div>
      )}

      {/* Founding Member callout */}
      {showFoundingCallout && (
        <div className="rounded-xl border border-amber-400/60 bg-gradient-to-br from-amber-50 to-amber-100/60 p-4 dark:from-amber-950/30 dark:to-amber-900/10">
          <div className="flex items-start gap-2.5">
            <Star className="mt-0.5 h-5 w-5 shrink-0 fill-amber-500 text-amber-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">
                Founding Member Offer — Lock in £3/month for life
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Usually £4.99/month. Only{" "}
                <strong className="text-foreground">{founding.remaining} of {founding.cap}</strong>{" "}
                spots left.
              </p>
            </div>
          </div>
        </div>
      )}

      {founding.soldOut && tier === "standard" && variant !== "expired-trial" && (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Founding Member offer is now closed — thank you to our 250 founders.
        </div>
      )}

      {/* Feature list */}
      <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
        <p className="text-sm font-semibold text-foreground">
          Your complete recipe and meal-planning system
        </p>
        <ul className="space-y-2.5 text-sm text-foreground">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex items-start gap-2.5">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                <strong className="font-semibold">{title}</strong> — {body}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Tier + interval selector (hide while checkout is mounted) */}
      {!showCheckout && (
        <div className="space-y-3">
          {/* Billing interval toggle */}
          <div className="flex items-center justify-center">
            <div
              role="tablist"
              aria-label="Billing interval"
              className="inline-flex rounded-full border border-border bg-muted/40 p-1"
            >
              <button
                role="tab"
                aria-selected={interval === "monthly"}
                onClick={() => setInterval("monthly")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  interval === "monthly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                role="tab"
                aria-selected={interval === "yearly"}
                onClick={() => setInterval("yearly")}
                className={`relative rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  interval === "yearly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Yearly
                <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {yearlySavingsLabel()}
                </span>
              </button>
            </div>
          </div>

          {/* Selected price card */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {tier === "founding" ? "Founding Member" : "Standard"}
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-semibold text-foreground">
                    {activePrice.label}
                  </span>
                  <span className="text-sm text-muted-foreground">{activePrice.subLabel}</span>
                  {tier === "founding" && (
                    <span className="text-xs text-muted-foreground line-through">
                      {STANDARD_ANCHOR[interval]}
                    </span>
                  )}
                </div>
              </div>
              {tier === "founding" && (
                <div className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                  <Check className="h-3 w-3" /> Locked in for life
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            No payment today. 14-day free trial. Cancel anytime.
          </p>
        </div>
      )}

      {isPaymentsTestMode() && paymentsReady && (
        <div className="rounded-lg border border-accent bg-accent/30 px-3 py-2 text-xs text-foreground">
          Test mode — use card <strong>4242 4242 4242 4242</strong>, any future expiry, any CVC.
        </div>
      )}

      {errorMsg && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {errorMsg}
        </div>
      )}

      {!paymentsReady ? (
        <PaymentsUnavailableNotice />
      ) : showCheckout ? (
        <CheckoutErrorBoundary>
          <div id="checkout" className="-mx-1">
            <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </CheckoutErrorBoundary>
      ) : onRevealCheckout ? (
        <button
          type="button"
          onClick={() => onRevealCheckout(selectedPriceId)}
          className="w-full rounded-xl bg-primary px-4 py-3 text-base font-semibold text-primary-foreground shadow-soft transition-smooth hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {primaryCta}
        </button>
      ) : null}
    </div>
  );
};
